import { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getMediaFiles, uploadMedia, deleteMedia } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Upload, Image as ImageIcon, File, Trash2, Loader2, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function MediaLibraryPage() {
  const { t, language } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const fetchFiles = async () => {
    try {
      const response = await getMediaFiles({});
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch media files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      await handleFilesUpload(Array.from(droppedFiles));
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      await handleFilesUpload(Array.from(selectedFiles));
    }
  };

  const handleFilesUpload = async (filesToUpload) => {
    setUploading(true);
    try {
      for (const file of filesToUpload) {
        await uploadMedia(file, null, null);
      }
      toast.success(t('success.uploaded'));
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingFile) return;
    
    try {
      await deleteMedia(deletingFile.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingFile(null);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) {
      return <ImageIcon className="w-8 h-8 text-blue-500" />;
    }
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) {
      return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
    }
    if (mimeType?.includes('json') || mimeType?.includes('text')) {
      return <FileText className="w-8 h-8 text-yellow-500" />;
    }
    return <File className="w-8 h-8 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6" data-testid="media-library-page">
      {/* Upload Section */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            {t('upload_media')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-[#0A4C3A] bg-[#0A4C3A]/5' 
                : 'border-gray-300 hover:border-[#0A4C3A]'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="media-dropzone"
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 animate-spin text-[#0A4C3A]" />
                <p className="text-sm text-gray-600">{t('loading')}</p>
              </div>
            ) : (
              <>
                <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-700 mb-2">{t('drop_file_here')}</p>
                <p className="text-sm text-gray-500 mb-4">
                  Images, Excel, JSON, PDF
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="media-upload"
                  data-testid="media-file-input"
                />
                <label htmlFor="media-upload">
                  <Button 
                    variant="outline" 
                    className="cursor-pointer"
                    asChild
                  >
                    <span>{t('select')}</span>
                  </Button>
                </label>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Files Grid */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle>{t('media_library')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {t('no_data')}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {files.map((file) => (
                <div 
                  key={file.id} 
                  className="group relative bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  data-testid={`media-file-${file.id}`}
                >
                  <div className="flex flex-col items-center">
                    <div className="mb-3">
                      {getFileIcon(file.mime_type)}
                    </div>
                    <p className="text-xs font-medium text-gray-700 text-center truncate w-full" title={file.filename}>
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatFileSize(file.file_size)}
                    </p>
                  </div>
                  
                  {/* Delete button overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => {
                        setDeletingFile(file);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-media-${file.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingFile?.filename}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-media"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
